import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiPlay, FiArrowRight, FiArrowLeft} = FiIcons;

const Step4 = ({ formData, updateFormData, onNext, onPrev }) => {
  const actionOptions = [
    'Write a short email about your book',
    'Post on social media about your core message',
    'Reach out to one person for support or a review',
    'Create a basic promo image in Canva',
    'Open your email platform + plan your first series'
  ];

  const handleActionSelect = (action) => {
    updateFormData({ firstStep: action });
  };

  const canProceed = formData.firstStep;

  return (
    <div className="max-w-3xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-xl p-8"
      >
        <div className="text-center mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-orange-500 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <SafeIcon icon={FiPlay} className="text-2xl text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            Step 4: Take Your First Marketing Action
          </h2>
          <p className="text-gray-600 mb-6">
            Let's build momentum—without stress. Choose one doable task you can finish in under 15 minutes.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <label className="block text-lg font-semibold text-gray-700 mb-4">
            Pick one action you could do today in 15 minutes or less:
          </label>
          
          <div className="space-y-3">
            {actionOptions.map((action, index) => (
              <motion.button
                key={action}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleActionSelect(action)}
                className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                  formData.firstStep === action
                    ? 'border-green-500 bg-green-50 text-green-800'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  <div className={`w-4 h-4 rounded-full border-2 mr-3 flex-shrink-0 ${
                    formData.firstStep === action
                      ? 'border-green-500 bg-green-500'
                      : 'border-gray-300'
                  }`}>
                    {formData.firstStep === action && (
                      <div className="w-full h-full rounded-full bg-white scale-50"></div>
                    )}
                  </div>
                  <span className="font-medium">{action}</span>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-blue-50 p-6 rounded-xl mb-8"
        >
          <h3 className="font-semibold text-blue-800 mb-2">
            Remember: You've Got This! 🙌
          </h3>
          <p className="text-blue-700">
            Marketing happens one step at a time. Choose your first step in connecting 
            your important creative work to people who need it.
          </p>
        </motion.div>

        <div className="flex justify-between">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onPrev}
            className="bg-gray-500 text-white px-6 py-3 rounded-xl font-semibold flex items-center hover:bg-gray-600 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Back
          </motion.button>

          <motion.button
            whileHover={{ scale: canProceed ? 1.05 : 1 }}
            whileTap={{ scale: canProceed ? 0.95 : 1 }}
            onClick={onNext}
            disabled={!canProceed}
            className={`px-8 py-3 rounded-xl font-semibold flex items-center transition-all ${
              canProceed
                ? 'bg-gradient-to-r from-green-600 to-blue-600 text-white hover:shadow-lg'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Create My Plan!
            <SafeIcon icon={FiArrowRight} className="ml-2" />
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default Step4;